import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface UseScrollToHashOptions {
  isLoading?: boolean;
  delay?: number;
  headerOffset?: number;
}

/**
 * Custom hook to automatically scroll to a section based on URL hash fragment
 * @param options Configuration options
 * @param options.isLoading - Don't scroll while data is loading (default: false)
 * @param options.delay - Delay in ms before scrolling to ensure DOM is ready (default: 100)
 * @param options.headerOffset - Offset from top to account for fixed headers (default: 200)
 */
export const useScrollToHash = (options: UseScrollToHashOptions = {}) => {
  const { 
    isLoading = false, 
    delay = 100, 
    headerOffset = 200 
  } = options;
  
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && location.hash) {
      const sectionId = location.hash.substring(1); // Remove the '#'
      
      // Wait for the content to render before scrolling
      const timer = setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const mainContainer = document.querySelector('[data-main-container]');
          if (mainContainer) {
            const elementTop = element.offsetTop;
            
            mainContainer.scrollTo({
              top: elementTop - headerOffset,
              behavior: 'smooth'
            });
          } else {
            // Fallback to window scroll if main container not found
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [isLoading, location.hash, delay, headerOffset]);
};
